<script setup lang="ts">
import type { Notification } from "~/apiclient/types.gen"
import type { DisplayNotification } from "~/models/displayNotification"
import { useGetNotifications } from "~/queries/notifications"
import { useGetUserSettings } from "~/queries/userSettings"

const notificationStore = useNotificationsStore()
const snackbar = ref()
provide("snackbar", snackbar)
const centrifuge = useCentrifuge()
const { envConfig } = useEnvConfig()

const { data: rawNotifications } = useGetNotifications()
useGetUserSettings()

const { t } = useI18n()
const notifications = computed(() => {
	const grouped: { [key: string]: DisplayNotification[] } = {}

	for (const notification of (rawNotifications.value?.items ?? []) as Notification[]) {
		const date = new Date(notification.timestamp ?? "").toLocaleDateString()
		const transformedNotification: DisplayNotification = {
			id: notification.id ?? undefined,
			read: notification.read ?? false,
			eventType: notification.event,
			releaseHardwareModel: notification.releaseHardwareModel,
			releaseVersion: notification.releaseVersion,
			title: t(notification.event ?? "", {
				count: Math.abs(+(notification.expiryDays ?? 0)),
				app: notification.applicationTitle ?? notification.applicationId ?? "App",
				expiryDate: new Date(notification.expiryDate ?? "").toLocaleString([], {
					dateStyle: "medium",
					timeStyle: "short"
				}),
				days: notification.expiryDays,
				module: notification.module ?? "Module"
			}),
			links: {
				deviceId: notification.deviceId?.toString(),
				fleet: notification.fleet?.toString() ?? notification.fleetId?.toString() ?? "",
				fleetId: notification.fleetId ?? "",
				eventId: notification.eventId?.toString()
			},
			time: new Date(notification.timestamp ?? "").toLocaleTimeString([], { timeStyle: "short" }),
			downloadLink: notification.downloadLink?.toString(),
			traceId: notification.traceId
		}
		if (date in grouped) grouped[date]?.push(transformedNotification)
		else grouped[date] = [transformedNotification]
	}
	return Object.entries(grouped) as [string, DisplayNotification[]][]
})

const notificationCount = computed(() => Number(rawNotifications.value?.unreadCount ?? 0))

onBeforeUnmount(() => {
	notificationStore.unsubscribeFromNotifications()
})

onMounted(async () => {
	centrifuge.initializeCentrifuge()
	notificationStore.subscribeToNotifications()
})
</script>

<template>
	<UDashboardGroup unit="rem">
		<BaseSideBar />
		<UDashboardPanel :ui="{
			body: 'sm:p-0 p-0 bg-background'
		}">
			<template #header>
				<UTheme :ui="{
					dashboardNavbar: {
						root: 'h-14 border-0 border-b border-gray-200 bg-white box-border px-5',
						left: 'flex items-center gap-4',
						right: 'flex items-center gap-2'
					}
				}">
					<UDashboardNavbar>
						<template #left>
							<UDashboardSidebarCollapse />
							<DmLogo class="h-7 shrink-0" />
							<TenantLogo>
								<template #prepend>
									<div class="w-px h-6 bg-gray-200 self-center" />
								</template>
							</TenantLogo>
						</template>
						<template #right>
							<NotificationBell :notifications="notifications" :notification-count="notificationCount"
								@mark-all-as-read="notificationStore.markAllAsRead"
								@mark-as-read="notificationStore.markAsRead" @clear-read="notificationStore.clearRead" />
							<ErrorNotifications />
							<div class="w-px h-6 bg-gray-200 mx-1" />
							<UserMenu />
						</template>
					</UDashboardNavbar>
				</UTheme>
			</template>

			<template #body>
				<div class="relative flex-1">
					<div class="max-w-[1440px] w-full mx-auto px-7 py-7 flex flex-col gap-5">
						<slot />
					</div>
				</div>
			</template>

			<template #footer>
				<div
					class="flex items-center justify-center gap-3 px-4 py-2 text-xs text-neutral bg-white border-t border-gray-200">
					<span>{{ envConfig.appVersion }}</span>
					<span class="w-px h-3 bg-gray-300" />
					<OmnectLink to="https://www.conplement.de/de/impressum">
						{{ $t("dashboard.imprint") }}
					</OmnectLink>
				</div>
			</template>
		</UDashboardPanel>
		<SnackBar />
	</UDashboardGroup>
</template>
